/**
 * Admin portal — split-bundle entry App.
 * Bundled into dist/admin/ via vite.config.admin.ts.
 * Lovable preview vẫn dùng src/App.tsx (full routes).
 */
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { PortalAppShell } from "@/shared/app/PortalAppShell";
import SafeRoute from "@/components/system/SafeRoute";
import ProtectedAdminRoute from "@/apps/admin/guards/ProtectedAdminRoute";
import { AdminProvider } from "@/apps/admin/contexts/AdminContext";
import { TestModeProvider } from "@/apps/admin/contexts/TestModeContext";
import { CurrencyProvider } from "@/shared/contexts/CurrencyContext";
import { WorkspaceProvider } from "@/apps/admin/contexts/WorkspaceContext";
import { portalPath } from "@/shared/app/portalPath";
import {
  AdminLogin, AdminDashboard, AdminTestUpload, AdminAgentDetail, AdminLeads,
  AdminOperations, AdminSettings, OperationsCenter, AdminVendors, VendorDetail,
  TransportBookingDetail, TripCaseDetail, LeadDetail, AdminGuests, GuestDetail,
  QuotationDetail, AdminLeadSubmissions,
  AdminQuotes, AdminCoordinationFees, AdminPackages, AdminPricingSeasons,
} from "@/apps/admin/routes";

const S = ({ name, children }: { name: string; children: React.ReactNode }) => (
  <SafeRoute name={name}>{children}</SafeRoute>
);

const AdminWithContext = ({ children }: { children: React.ReactNode }) => (
  <WorkspaceProvider>
    <TestModeProvider>
      <CurrencyProvider>
        <AdminProvider>{children}</AdminProvider>
      </CurrencyProvider>
    </TestModeProvider>
  </WorkspaceProvider>
);

const LegacyTripCase = () => {
  const { caseId } = useParams<{ caseId: string }>();
  return <Navigate to={caseId ? `/admin/trip-case/${caseId}` : "/admin/operations"} replace />;
};
const LegacyPatientToGuest = () => {
  const { patientId } = useParams<{ patientId: string }>();
  return <Navigate to={patientId ? `/admin/guests/${patientId}` : "/admin/guests"} replace />;
};
const LegacyLead = () => {
  const { leadId } = useParams<{ leadId: string }>();
  return <Navigate to={leadId ? `/admin/leads/${leadId}` : "/admin/leads"} replace />;
};
const LegacyQuotation = () => {
  const { leadId, quotationId } = useParams<{ leadId: string; quotationId: string }>();
  return <Navigate to={leadId && quotationId ? `/admin/leads/${leadId}/quotation/${quotationId}` : "/admin/leads"} replace />;
};

// Dual-path route helper: registers both canonical (no prefix, for subdomain)
// and prefixed (/admin/..., for Lovable preview) variants.
const Dual = ({ path, element }: { path: string; element: React.ReactNode }) => (
  <>
    <Route path={path} element={element} />
    <Route path={`/admin${path === "/" ? "" : path}`} element={element} />
  </>
);

const AdminApp = () => {
  const p = portalPath("admin");
  const guard = (roles: any, el: React.ReactNode, _module?: string) => (
    <ProtectedAdminRoute allowedRoles={roles}>
      <AdminWithContext>{el}</AdminWithContext>
    </ProtectedAdminRoute>
  );
  const ops = ['admin','ops','bod_readonly'] as any;
  return (
  <PortalAppShell portal="admin">
    <Routes>
      {/* Root: redirect to login (guard will forward to dashboard if already authed) */}
      <Route path="/" element={<Navigate to={p("/login")} replace />} />
      <Route path="/admin" element={<Navigate to={p("/dashboard")} replace />} />

      {Dual({ path: "/login", element: <S name="AdminLogin"><AdminLogin /></S> })}
      {Dual({ path: "/dashboard", element: guard(ops, <S name="Dashboard"><AdminDashboard /></S>, "dashboard") })}
      {Dual({ path: "/leads", element: guard(ops, <S name="Leads"><AdminLeads /></S>, "leads") })}
      {Dual({ path: "/lead-submissions", element: guard(ops, <S name="LeadSubmissions"><AdminLeadSubmissions /></S>, "lead_submissions") })}
      {Dual({ path: "/quotes", element: guard(ops, <S name="Quotes"><AdminQuotes /></S>, "quotes") })}
      {Dual({ path: "/quote-approvals", element: <Navigate to={p("/quotes") + "?tab=approvals"} replace /> })}
      {Dual({ path: "/leads/:leadId", element: guard(ops, <S name="LeadDetail"><LeadDetail /></S>, "leads") })}
      {Dual({ path: "/leads/:leadId/quotation/:quotationId", element: guard(ops, <S name="Quotation"><QuotationDetail /></S>, "leads") })}
      {Dual({ path: "/messages", element: <Navigate to={p("/leads")} replace /> })}
      {Dual({ path: "/messages/:id", element: <Navigate to={p("/leads")} replace /> })}
      {Dual({ path: "/guests", element: guard(ops, <S name="Guests"><AdminGuests /></S>, "guests") })}
      {Dual({ path: "/guests/:guestId", element: guard(ops, <S name="GuestDetail"><GuestDetail /></S>, "guests") })}
      {Dual({ path: "/patients", element: <Navigate to={p("/guests")} replace /> })}
      {Dual({ path: "/patients/:patientId", element: <LegacyPatientToGuest /> })}
      {Dual({ path: "/travel-support", element: <Navigate to={p("/operations")} replace /> })}
      {Dual({ path: "/patient-experience", element: <Navigate to={p("/operations")} replace /> })}
      {Dual({ path: "/ops-hub", element: <Navigate to={p("/operations")} replace /> })}
      {Dual({ path: "/operations", element: guard(ops, <S name="Operations"><OperationsCenter /></S>, "operations_center") })}
      {Dual({ path: "/vendors", element: guard(ops, <S name="Vendors"><AdminVendors /></S>, "vendors") })}
      {Dual({ path: "/vendors/:vendorId", element: guard(ops, <S name="VendorDetail"><VendorDetail /></S>, "vendors") })}
      {Dual({ path: "/transport-master-data", element: <Navigate to={p("/vendors") + "?module=transport"} replace /> })}
      {Dual({ path: "/patient-experience/:caseId", element: <LegacyTripCase /> })}
      {Dual({ path: "/transport-booking/:bookingId", element: guard(ops, <S name="TransportBooking"><TransportBookingDetail /></S>, "operations_center") })}
      {Dual({ path: "/operations/trip/:caseId", element: <LegacyTripCase /> })}
      {Dual({ path: "/trip-case/:caseId", element: guard(ops, <S name="TripCase"><TripCaseDetail /></S>, "operations_center") })}
      {Dual({ path: "/lead/:leadId", element: <LegacyLead /> })}
      {Dual({ path: "/lead/:leadId/quotation/:quotationId", element: <LegacyQuotation /> })}
      {Dual({ path: "/ops-dashboard", element: <Navigate to={p("/dashboard")} replace /> })}
      {Dual({ path: "/test-upload", element: guard(['admin'], <S name="TestUpload"><AdminTestUpload /></S>) })}
      {Dual({ path: "/agent/:agentCode", element: guard(['admin'], <S name="AgentDetail"><AdminAgentDetail /></S>) })}
      {Dual({ path: "/settings", element: guard(['admin'], <S name="Settings"><AdminSettings /></S>, "settings") })}
      {Dual({ path: "/coordination-fees", element: guard(ops, <S name="CoordinationFees"><AdminCoordinationFees /></S>, "coordination_fees") })}
      {Dual({ path: "/packages", element: guard(ops, <S name="Packages"><AdminPackages /></S>, "packages") })}
      {Dual({ path: "/pricing-seasons", element: guard(ops, <S name="PricingSeasons"><AdminPricingSeasons /></S>, "pricing_seasons") })}
      {/* Legacy Settings-tab URLs → standalone routes */}
      {Dual({ path: "/settings/coordination-fees", element: <Navigate to={p("/coordination-fees")} replace /> })}
      {Dual({ path: "/settings/packages", element: <Navigate to={p("/packages")} replace /> })}
      {Dual({ path: "/settings/pricing-seasons", element: <Navigate to={p("/pricing-seasons")} replace /> })}
      {Dual({ path: "/catalog", element: <Navigate to={p("/operations")} replace /> })}
      {Dual({ path: "/accommodation", element: <Navigate to={p("/operations")} replace /> })}
      {Dual({ path: "/transport", element: <Navigate to={p("/operations")} replace /> })}
      {Dual({ path: "/hotels", element: <Navigate to={p("/operations")} replace /> })}
      {Dual({ path: "/tours", element: <Navigate to={p("/operations")} replace /> })}
      {Dual({ path: "/system-audit", element: <Navigate to={p("/settings")} replace /> })}
      {Dual({ path: "/database-schema", element: <Navigate to={p("/settings")} replace /> })}
      <Route path="*" element={<Navigate to={p("/login")} replace />} />
    </Routes>
  </PortalAppShell>
  );
};

export default AdminApp;
